import { IGameState } from 'State/Game/IGameState';
import { ICardModifierData } from '../../../Prototype/Level/Card/ICardModifierData';
import CardStateHelper from '../CardStateHelper';
import { ICardsState, ICardStateData } from '../ICardState';

export default class BaseModifierBehavior {
    public modifier: ICardModifierData;
    public cardID: string;

    constructor(modifier: ICardModifierData, cardID: string) {
        this.modifier = modifier;
        this.cardID = cardID;
    }

    /**
     * Called for each modifier after an in-play card is matched.
     * @param state Card state reference.
     * @param matchedCardID The ID of the card triggering this update.
     */
    public onCardMatched(
        state: IGameState,
        matchedCardID: string
    ): IGameState {
        return state;
    }

    /**
     * Called for each modifier after an in-play card is folded and after onCardMatched() is called for each modifier.
     * Example: Bomb cards must all be cleared first before additional cards are added to in play - therefore the
     * bomb cards are cleared in onCardMatched() where the new cards are added in onCardMatchedLate()
     * @param state
     */
    public onCardMatchedLate(state: ICardsState): ICardsState {
        return state;
    }

    /**
     * Called for each modifier after a card is folded.
     * @param state Card state reference.
     * @param sourceCardID The ID of the card triggering this update.
     */
    public onCardFolded(state: IGameState, sourceCardID: string): IGameState {
        return state;
    }

    /**
     * Called for each modifier after an undo card is used.
     * @param state Cards state reference.
     * @param prevCardState Card state before the last move.
     * @param prevModifier Card modifier data before the last move.
     */
    public onUndoUsed(state: ICardsState, prevCardState: ICardStateData, prevModifier: ICardModifierData): ICardsState {
        return state;
    }

    public updateModifier(
        state: ICardsState,
        cardID: string,
        modifierData: Partial<ICardModifierData>
    ): ICardsState {
        state = {
            ...state,
            cards: {
                ...state.cards,
                [cardID]: {
                    ...state.cards[cardID],
                    modifiers: state.cards[cardID].modifiers.map(m => {
                        if (m !== this.modifier) {
                            return m;
                        }

                        this.modifier = {
                            ...this.modifier,
                            ...modifierData,
                            properties: {
                                ...this.modifier.properties,
                                ...modifierData.properties,
                            },
                        };

                        return this.modifier;
                    }),
                },
            },
        };
        return state;
    }

    /**
     * This method is also invoked when this modifier's card is played and this 'overrideCardPlay()' returns true.
     * @param state
     */
    public play(state: IGameState): IGameState {
        return state;
    }

    /**
     * Called when this modifier's card is played.
     * @param state
     */
    public onOwnCardPlayed(state: ICardsState): ICardsState {
        return state;
    }

    /**
     * Called when this modifier's card is discarded.
     * @param state
     */
    public onOwnCardDiscarded(state: ICardsState): ICardsState {
        return state;
    }

    /**
     * Returns true if this modifier prevents playing the card, otherwise returns false.
     * @param state
     * @param modifier The modifier data reference
     */
    public isBlocking(state: ICardsState): boolean {
        return false;
    }

    public overrideCardPlay(state: ICardsState): boolean {
        return false;
    }

    public fillFlipDelays(cardsState: ICardsState, delays: Record<string, number>): void {
        return;
    }

    protected validateUpdate(state: ICardsState): boolean {
        // Must be in-play to trigger countdown.
        if (state.inPlay.indexOf(this.cardID) === -1) return false;

        const behavior = CardStateHelper.getCardBehavior(state, this.cardID);

        // Card must be face-up.
        if (!behavior.isFaceUp(state)) return false;

        // Card must not be obscured.
        if (behavior.isObscured(state)) return false;

        return true;
    }
}
